/**
 * Import Sync Service
 * Real API implementation connecting to backend.
 *
 * Start / pause sync operations for a configured source. Split out of
 * import.service.ts; bodies moved verbatim.
 */

import { api, throwBackendError } from '@/platform/api/api.service'
import { useMonitoringStore } from '@/platform/sync/stores/monitoringStore'
import { IMPORT_ROUTES } from '@/shared/constants/config-routes'
import { ApiException } from '@/shared/utils/api'

import { SyncImportSourceResponse } from '../types'
import { type ImportSource } from './import-service-shared'
import { getSources } from './sources.service'
import { resolveSourceUuid } from '../utils/resolve-source-uuid'

/**
 * Options accepted by `syncSource`.
 * - `forceFullResync`: when true, the backend ignores `lastSyncedAt` and re-pulls
 *   the full history for this source. Default (undefined/false) is the normal
 *   incremental sync.
 */
export interface SyncSourceOptions {
  forceFullResync?: boolean
  /** Name the live panel shows when only the id is at hand (the panel's own retry). */
  displayName?: string
}

/**
 * Start sync for a source (fire-and-forget — polling handled by hook).
 * Resolves to the 202 body (`jobId`, `sourceId`, `syncStartedAt`) so the
 * caller can follow THAT job; null when the server answered without one.
 * @param sourceOrId - Either the ImportSource object (preferred) or numeric ID (deprecated)
 * @param options - Optional sync modifiers (e.g. `{ forceFullResync: true }`)
 */
export const syncSource = async (
  sourceOrId: ImportSource | string,
  options?: SyncSourceOptions,
): Promise<SyncImportSourceResponse | null> => {
  const uuid = resolveSourceUuid(sourceOrId)

  // Only send a body when explicitly forcing a full resync. The backend's
  // existing happy path accepts an empty body, and Fastify is strict about
  // `Content-Type: application/json` with no body, so we keep the
  // default-path behavior byte-for-byte identical.
  const body = options?.forceFullResync === true ? { forceFullResync: true } : {}
  const response = await api.post<SyncImportSourceResponse>(IMPORT_ROUTES.SOURCES.SYNC(uuid), body)

  if (!response.success) {
    // api-helper converts thrown ApiExceptions into `{ success: false, error:
    // { message, code, statusCode } }`. throwBackendError re-throws preserving
    // message, code, statusCode AND details so the UI (and the RESOURCE_BUSY
    // detection in useSourceSync) sees the backend's actual reason.
    throwBackendError(response, 'IMPORT_SYNC_FAILED')
  }

  const accepted = response.data ?? null
  // The job is ACCEPTED from here on: nothing below may turn this call into a
  // failure (a caller would toast an error for an import that is running).
  try {
    const monitoring = useMonitoringStore.getState()
    // The panel shows the job the instant the 202 is read, as queued; the
    // status fetch only brings its server-side details. The store coalesces
    // concurrent fetches, so a bulk run of N sources costs ≤ 2.
    if (accepted?.jobId) {
      monitoring.registerAcceptedJob({
        jobId: accepted.jobId,
        sourceId: accepted.sourceId ?? null,
        sourceName: options?.displayName ?? (typeof sourceOrId === 'object' ? sourceOrId.name : ''),
        type: typeof sourceOrId === 'object' ? panelSyncType(sourceOrId.type) : 'api',
        startedAt: accepted.syncStartedAt || new Date().toISOString(),
      })
    }
    // Fire-and-forget: the store records its own errors (statusError).
    void monitoring.fetchSyncStatus()
  } catch {
    // The panel's poll catches up; the import itself was accepted.
  }
  return accepted
}

/** The source kinds of the sources table, in the monitoring vocabulary. */
const panelSyncType = (type: ImportSource['type']): 'api' | 'wallet' | 'csv' =>
  type === 'blockchain' ? 'wallet' : type

/**
 * Lo que devuelve `pauseSync`. Medido en e2e (2026-09-15): la página cantaba
 * «Sincronización … pausada» sobre una respuesta `{ paused: 0 }` y la
 * importación terminó 50 s después. Una importación que ya se está EJECUTANDO no
 * se puede pausar (el backend la rechaza con JOB_RUNNING_NOT_PAUSABLE y la
 * cuenta en `running`), así que la fila necesita saber las dos cosas:
 *  - `pausedJobs`: trabajos en cola que de verdad quedaron retenidos;
 *  - `runningJobs`: trabajos ya en marcha, que terminarán solos;
 *  - `source`: la fuente releída, con el estado que el servidor publica AHORA.
 */
export interface PauseSyncOutcome {
  pausedJobs: number
  runningJobs: number
  source: ImportSource
}

/**
 * Pause sync
 * @param sourceOrId - Either the ImportSource object (preferred) or numeric ID (deprecated)
 */
export const pauseSync = async (sourceOrId: ImportSource | string): Promise<PauseSyncOutcome> => {
  const uuid = resolveSourceUuid(sourceOrId)
  const stringId = typeof sourceOrId === 'object' ? sourceOrId.id : sourceOrId

  const response = await api.post(IMPORT_ROUTES.SOURCES.PAUSE(uuid))

  // api.post never throws — surface the backend error so the caller's catch
  // shows a real failure instead of pretending the pause worked.
  if (!response.success) {
    throwBackendError(response, 'IMPORT_SYNC_PAUSE_FAILED')
  }

  // Leído defensivamente, como `data.resumed` en `resumeSync`: ausente cuenta
  // como 0, que es el caso conservador (no se anuncia ninguna pausa).
  const payload = response.data as { paused?: number; running?: number } | undefined
  const pausedJobs = typeof payload?.paused === 'number' ? payload.paused : 0
  const runningJobs = typeof payload?.running === 'number' ? payload.running : 0

  // Get updated source status
  const sources = await getSources()
  const updatedSource = sources.find((s) => s.id === stringId)

  if (!updatedSource) {
    throw new ApiException({
      status: 0,
      code: 'IMPORT_SOURCE_NOT_FOUND_AFTER_PAUSE',
      message: '',
    })
  }

  return { pausedJobs, runningJobs, source: updatedSource }
}

/**
 * Lo que devuelve `resumeSync`. Son DOS datos porque la fila necesita los dos y
 * uno no se deduce del otro:
 *  - `resumedJobs` es el recuento que da el backend (`data.resumed`). Un 0
 *    significa que ya no quedaba ningún trabajo `paused` que retomar — el
 *    trabajo terminó o se canceló entre el pintado de la fila y el clic — y
 *    anunciar «reanudada» en ese caso sería una confirmación que miente.
 *  - `source` es la fuente releída, con el estado que el servidor publica AHORA.
 */
export interface ResumeSyncOutcome {
  resumedJobs: number
  source: ImportSource
}

/**
 * Resume sync — gemelo de `pauseSync`.
 * @param sourceOrId - Either the ImportSource object (preferred) or the id string
 */
export const resumeSync = async (sourceOrId: ImportSource | string): Promise<ResumeSyncOutcome> => {
  const uuid = resolveSourceUuid(sourceOrId)
  const stringId = typeof sourceOrId === 'object' ? sourceOrId.id : sourceOrId

  const response = await api.post(IMPORT_ROUTES.SOURCES.RESUME(uuid))

  // api.post never throws — surface the backend error so the caller's catch
  // shows a real failure instead of pretending the resume worked.
  if (!response.success) {
    throwBackendError(response, 'IMPORT_SYNC_RESUME_FAILED')
  }

  // `data.resumed` lo escribe `syncHandlers.resumeSync` a partir de
  // `resumeJobsForSource`. Se lee defensivamente porque el contrato de
  // `api.post` es genérico; ausente cuenta como 0, que es el caso conservador.
  const payload = response.data as { resumed?: number } | undefined
  const resumedJobs = typeof payload?.resumed === 'number' ? payload.resumed : 0

  const sources = await getSources()
  const updatedSource = sources.find((s) => s.id === stringId)

  if (!updatedSource) {
    throw new ApiException({
      status: 0,
      code: 'IMPORT_SOURCE_NOT_FOUND_AFTER_RESUME',
      message: '',
    })
  }

  return { resumedJobs, source: updatedSource }
}
